// baseQuery do RTK Query. Prefixo /api/v1 (nginx + URI versioning §22).
// Em 401, tenta refresh uma vez; se o refresh for recusado de forma DEFINITIVA,
// encerra a sessão. Se falhar por rede/5xx, o erro é propagado e a sessão fica de pé.
import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { getToken, clearAccessToken, clearSession } from '../auth/token-storage';
import { refreshAccessToken } from '../auth/refresh';
import { scheduleTokenRefresh, stopTokenRefreshScheduler } from '../auth/refresh-scheduler';
import { sessionEnded } from '@/features/auth/slice';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '/api/v1',
  credentials: 'include',
  prepareHeaders: (headers, { arg }) => {
    const token = getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    // Quando body é FormData (upload de CSV, etc.), o browser precisa setar
    // Content-Type com o boundary correto — não devemos forçar application/json.
    const body =
      typeof arg === 'object' && arg !== null ? (arg as { body?: unknown }).body : undefined;
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    if (!isFormData && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  },
});

/**
 * Endpoints públicos que respondem 401 por motivo PRÓPRIO, não por sessão expirada:
 * senha errada, convite inválido, token de reset queimado. Passar esses 401 pelo
 * interceptor fazia o app disparar um refresh a cada senha errada na tela de login e,
 * quando ele falhava (o normal para quem ainda não entrou), apagar a flag de sessão
 * de quem só errou a senha.
 */
const REFRESH_EXEMPT = [
  'auth/login',
  'auth/refresh',
  'auth/signup',
  'auth/invite/accept',
  'auth/password-reset/confirm',
  // Cobre `auth/google` e `auth/google/signup` (o casamento é por prefixo). Sem
  // isto, um consentimento recusado ou um e-mail sem conta dispararia refresh — e
  // derrubaria a sessão de quem já estava logado noutra aba.
  'auth/google',
];

// Exportada para teste: a lista é uma decisão de segurança (um 401 de senha errada
// não pode derrubar a sessão de outra aba) e não tinha nenhum caso a segurando.
export function isRefreshExempt(args: string | FetchArgs): boolean {
  const url = typeof args === 'string' ? args : args.url;
  const normalized = url.replace(/^\//, '');
  return REFRESH_EXEMPT.some((path) => normalized.startsWith(path));
}

export const baseQueryWithRefresh: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401 && !isRefreshExempt(args)) {
    const refreshed = await refreshAccessToken();

    if (refreshed.ok) {
      // Token novo, prazo novo: reancora a renovação antecipada no `exp` atual.
      scheduleTokenRefresh();
      result = await rawBaseQuery(args, api, extraOptions);
    } else if (refreshed.reason === 'auth') {
      stopTokenRefreshScheduler();
      // Recusa definitiva do servidor: a sessão acabou. Limpa tudo e avisa o Redux,
      // pra `RequireAuth` levar ao /login em vez de deixar a UI zumbi — logada na
      // aparência, com todas as requisições falhando.
      clearSession();
      api.dispatch(sessionEnded());
    } else {
      // Falha transitória (rede, 5xx, 429). O access token segue inutilizável, mas a
      // sessão continua: descarta só o token e PRESERVA a flag, para o próximo boot
      // conseguir retomar pelo cookie httpOnly. O erro sobe para a UI tratar.
      clearAccessToken();
    }
  }

  return result;
};
